import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FiUser, FiSliders, FiLoader } from 'react-icons/fi';
import api from '../../../api/api';
import { IGlobalState } from '../../../model/IGlobalState';
import { SaveProfileRequest } from '../../../model/ProfileResult';
import { SetUser } from '../../../actions/SetUser';
import { to } from '../../../util/util';
import { Avatar } from '../Header';
import Dropdown from '../Dropdown';
import { toast } from '../Toast';
import AccountNav from './AccountNav';
import ChangeEmailModal from './ChangeEmailModal';

const MAX_PHOTO_BYTES = 5 * 1024 * 1024; // mirrors ProfileController.MaxPhotoBytes

const BROWSER_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const TIME_ZONES: string[] = (Intl as any).supportedValuesOf?.('timeZone') ?? [BROWSER_TIME_ZONE];
// "Australia/Perth (AWST)" — the abbreviation as the browser names it today
const zoneLabel = (tz: string) => {
    const abbr = new Intl.DateTimeFormat('en-AU', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date()).find(x => x.type === 'timeZoneName')?.value;
    return abbr ? `${tz} (${abbr})` : tz;
};
const TIME_ZONE_OPTIONS = TIME_ZONES.map(tz => ({ value: tz, label: zoneLabel(tz) }));

const DATE_FORMATS = [
    { value: 'DD/MM/YYYY', label: '31/12/2026 (DD/MM/YYYY)' },
    { value: 'MM/DD/YYYY', label: '12/31/2026 (MM/DD/YYYY)' },
    { value: 'YYYY-MM-DD', label: '2026-12-31 (YYYY-MM-DD)' },
];
const LANGUAGES = [
    { value: 'en-AU', label: 'English (Australia)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'en-US', label: 'English (US)' },
];
const TEXT_SIZES = [
    { value: 'standard', label: 'Standard' },
    { value: 'large', label: 'Large' },
    { value: 'extra-large', label: 'Extra large' },
];

// "+61 4•• ••• 118": keep the first 4 and last 3 characters, hide the digits between
const maskMobile = (m: string) => m.length <= 7 ? m : m.slice(0, 4) + m.slice(4, -3).replace(/\d/g, '•') + m.slice(-3);

const errorText = (err: any, fallback: string) => typeof err?.response?.data === 'string' ? err.response.data : fallback;

// '' and null both mean "not set", so clearing a never-set field isn't a change
const isChanged = (a: SaveProfileRequest, b: SaveProfileRequest) =>
    (Object.keys(a) as (keyof SaveProfileRequest)[]).some(k => (a[k] || null) !== (b[k] || null));

const MyProfile: React.FC = () => {
    const user = useSelector((state: IGlobalState) => state.user);
    const dispatch = useDispatch();
    const [form, setForm] = useState<SaveProfileRequest | null>(null);
    // last saved values; Save Changes is enabled only when form differs
    const [savedForm, setSavedForm] = useState<SaveProfileRequest | null>(null);
    const [email, setEmail] = useState('');
    const [clients, setClients] = useState<{ id: string; name: string }[]>([]);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [photoBusy, setPhotoBusy] = useState(false);
    const [editingMobile, setEditingMobile] = useState(false);
    const [changingEmail, setChangingEmail] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        api().getProfile().then(p => {
            const loaded: SaveProfileRequest = {
                firstName: p.firstName, lastName: p.lastName, mobile: p.mobile, jobTitle: p.jobTitle, organisation: p.organisation,
                timeZone: p.timeZone ?? BROWSER_TIME_ZONE, dateFormat: p.dateFormat ?? DATE_FORMATS[0].value, language: p.language ?? LANGUAGES[0].value,
                defaultStockId: p.defaultStockId, textSize: p.textSize ?? 'standard',
            };
            setEmail(p.username);
            setForm(loaded);
            setSavedForm(loaded);
            setEditingMobile(!p.mobile);
        }).catch(() => setLoadError('Could not load your profile'));
        api().getClientDashboardResult().then(r => setClients(r.stocks
            .map(s => ({ id: s.id, name: s.companyName || s.combinedTicker }))
            .sort((a, b) => a.name.localeCompare(b.name))));
    }, []);

    const set = (field: keyof SaveProfileRequest, value: string) => setForm(f => f && { ...f, [field]: value });

    const dirty = !!form && !!savedForm && isChanged(form, savedForm);

    const save = async () => {
        if (!form) return;
        setSaving(true);
        const [err, updated] = await to(api().saveProfile(form));
        setSaving(false);
        if (err) {
            toast.error(errorText(err, 'Could not save your profile'));
            return;
        }
        dispatch(SetUser(updated));
        setSavedForm(form);
        toast.success('Profile saved');
    };

    const uploadPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!['image/jpeg', 'image/png'].includes(file.type) || file.size > MAX_PHOTO_BYTES) {
            toast.error('Choose a JPG or PNG up to 5 MB');
            return;
        }
        setPhotoBusy(true);
        const [err, updated] = await to(api().uploadProfilePhoto(file));
        setPhotoBusy(false);
        if (err) {
            toast.error(errorText(err, 'Could not upload the photo'));
            return;
        }
        dispatch(SetUser(updated));
        toast.success('Photo updated');
    };

    const removePhoto = async () => {
        setPhotoBusy(true);
        const [err, updated] = await to(api().removeProfilePhoto());
        setPhotoBusy(false);
        if (err) {
            toast.error(errorText(err, 'Could not remove the photo'));
            return;
        }
        dispatch(SetUser(updated));
        toast.success('Photo removed');
    };

    return <>
        <div className="page-header">
            <div>
                <h1>My Profile</h1>
                <p>Your name and details as your team and clients see them</p>
            </div>
            <button type="button" className="btn-primary" onClick={save} disabled={!dirty || saving}>
                {saving && <FiLoader className="spin" />} {saving ? 'Saving…' : 'Save Changes'}
            </button>
        </div>

        <div className="account-row">
            <AccountNav />

            <div className="account-main">
                {loadError && <p className="error">{loadError}</p>}
                {form && <>
                    <section className="card">
                        <div className="section-title"><span className="icon-tile"><FiUser /></span><h2>Personal Details</h2></div>

                        <div className="photo">
                            <Avatar user={user} className="avatar large" />
                            <div>
                                <div className="title">Profile photo</div>
                                <div className="hint">JPG or PNG, at least 400 × 400 px</div>
                                <div className="actions">
                                    <button type="button" className="btn-outline" onClick={() => fileRef.current?.click()} disabled={photoBusy}>
                                        {photoBusy && <FiLoader className="spin" />} Upload Photo
                                    </button>
                                    {user.photoVersion && <button type="button" className="remove" onClick={removePhoto} disabled={photoBusy}>Remove</button>}
                                    <input ref={fileRef} type="file" accept="image/jpeg,image/png" hidden onChange={uploadPhoto} />
                                </div>
                            </div>
                        </div>

                        <div className="fields">
                            <label className="field">
                                <span className="label">First name</span>
                                <input value={form.firstName ?? ''} onChange={e => set('firstName', e.target.value)} />
                            </label>
                            <label className="field">
                                <span className="label">Last name</span>
                                <input value={form.lastName ?? ''} onChange={e => set('lastName', e.target.value)} />
                            </label>
                            <div className="field">
                                <span className="label">Email address <em>used to sign in</em></span>
                                <div className="value-box">
                                    <span>{email}</span>
                                    <button type="button" className="link" onClick={() => setChangingEmail(true)}>Change</button>
                                </div>
                            </div>
                            <div className="field">
                                <span className="label">Mobile number <em>for security codes</em></span>
                                {editingMobile
                                    ? <input type="tel" value={form.mobile ?? ''} onChange={e => set('mobile', e.target.value)} placeholder="+61 4xx xxx xxx" />
                                    : <div className="value-box">
                                        <span>{maskMobile(form.mobile ?? '')}</span>
                                        <button type="button" className="link" onClick={() => setEditingMobile(true)}>Change</button>
                                    </div>}
                            </div>
                            <label className="field">
                                <span className="label">Job title</span>
                                <input value={form.jobTitle ?? ''} onChange={e => set('jobTitle', e.target.value)} />
                            </label>
                            <label className="field">
                                <span className="label">Organisation</span>
                                <input className="subtle" value={form.organisation ?? ''} onChange={e => set('organisation', e.target.value)} />
                            </label>
                        </div>
                    </section>

                    <section className="card">
                        <div className="section-title"><span className="icon-tile"><FiSliders /></span><h2>Preferences</h2></div>

                        <div className="fields">
                            {/* div, not label: a label would re-click the dropdown button */}
                            <div className="field">
                                <span className="label">Time zone</span>
                                <Dropdown searchable value={form.timeZone ?? ''} options={TIME_ZONE_OPTIONS} onChange={v => set('timeZone', v)} />
                            </div>
                            <div className="field">
                                <span className="label">Date format</span>
                                <Dropdown value={form.dateFormat ?? ''} options={DATE_FORMATS} onChange={v => set('dateFormat', v)} />
                            </div>
                            <div className="field">
                                <span className="label">Language</span>
                                <Dropdown value={form.language ?? ''} options={LANGUAGES} onChange={v => set('language', v)} />
                            </div>
                            <div className="field">
                                <span className="label">Default client when you sign in</span>
                                <Dropdown searchable={clients.length > 8} value={form.defaultStockId ?? ''} onChange={v => set('defaultStockId', v)}
                                    options={[{ value: '', label: 'First client in the list' }, ...clients.map(x => ({ value: x.id, label: x.name }))]} />
                            </div>
                        </div>

                        <div className="text-size">
                            <div>
                                <div className="title">Text size</div>
                                <div className="hint">Make everything in Investi easier to read</div>
                            </div>
                            <div className="segmented">
                                {TEXT_SIZES.map(x => <button key={x.value} type="button" className={form.textSize === x.value ? 'active' : ''}
                                    onClick={() => set('textSize', x.value)}>{x.label}</button>)}
                            </div>
                        </div>
                    </section>
                </>}
            </div>
        </div>

        {changingEmail && <ChangeEmailModal currentEmail={email} onClose={() => setChangingEmail(false)}
            onChanged={newEmail => { setEmail(newEmail); setChangingEmail(false); }} />}
    </>;
};

export default MyProfile;
